import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 캘린더 UI 상태 (T-cal20) — themeStore 동형(zustand + persist).
 *
 *   - activeView: 'month' | 'week' | 'day' — 현재 뷰.
 *   - anchorDateIso: 기준일(YYYY-MM-DD). 뷰 이동의 중심. Date 직접 저장 대신 ISO string(persist 직렬화).
 *   - visibleCalendarIds: 표시 캘린더 id 집합. null = 전체 표시(미초기화 폴백, T-cal36 회귀 안전).
 *       ⛔ **2026-09-30 정정** — 종전 줄은 「빈 배열([])은 "전부 숨김"이 아니라 … null=전체 정책」
 *       이었다. **그것이 결함이었고, 「정책」이라 적혀 있어서 아무도 다시 안 쟀다.**
 *       ⇒ 이제 **`[]` 는 「전부 숨김」이다.** `null`(미초기화·전체)과 뜻이 다르다.
 *         판정은 `lib/calendar-visibility` 한 곳에 있다 — 여기서 다시 세지 말 것.
 *       ⚠ 그리고 이 배열에는 **`NO_CALENDAR`(캘린더를 안 고른 일정) 통**도 든다.
 *         그 통을 빼고 서버에 `calendarIds` 를 보내면 SQL 의 `IN` 이 NULL 을 빼서
 *         그 일정들이 통째로 사라진다 — 그것이 함께 고친 결함이다.
 *       사이드바가 토글하며, 비어있으면(=null) listEvents 를 calendarIds 없이 호출 → 전체(NULL 포함).
 *
 * persist 키 workchat:calendar — 새로고침 시 뷰·기준일·표시 선호 유지(themeStore workchat:theme 정책 동형).
 */
export type CalendarView = 'month' | 'week' | 'day';

interface CalendarState {
  activeView: CalendarView;
  anchorDateIso: string; // YYYY-MM-DD
  visibleCalendarIds: string[] | null;
  /**
   * 「내가 포함된 일정만」 — 작성자거나 참석자이거나 팀원인 것만 그린다(2026-09-09 결정).
   * 필터 팝오버의 스위치가 토글한다.
   */
  onlyMine: boolean;
  /**
   * 「팀원 일정도 보기」 — 필터 팝오버의 스위치가 토글한다. **기본 꺼짐**(옛 가지 5b3fa6597 결정).
   *   ⭐ 2026-10-13 — 만료 조건이 찼다: 서버 `GET /calendar/events` 가 `teamMembers=1` 을 받는다
   *     (`calendar-list-query.ts`). 켜면 `listEvents` 다섯째 인자로 넘어가고, 내 팀(조직도팀·프로젝트팀)의
   *     «다른» 구성원이 만들었거나 참석하는 일정이 함께 온다.
   *   ⚠ 이름은 종전 그대로다 — persist 에 이미 저장된 값(대개 false)을 그대로 읽는다.
   */
  showTeamMembers: boolean;
  /**
   * 끈 유형(meeting·todo …). 여기 든 유형은 그리드에서 뺀다.
   * ⭐ 이 스토어가 이미 persist 라 «새로고침을 넘어» 남는다 — 원문의 「유형 필터 영속화」가 그것이다.
   */
  hiddenTypes: string[];

  setView: (view: CalendarView) => void;
  setAnchorDate: (date: Date) => void;
  toggleOnlyMine: () => void;
  toggleShowTeamMembers: () => void;
  toggleType: (type: string) => void;
  /** 표시 캘린더 토글 — null(전체)에서 첫 토글 시 해당 id 제외 의미가 모호하므로, 명시 set 권장. */
  toggleCalendarVisible: (id: string, allIds: string[]) => void;
  setVisibleCalendarIds: (ids: string[] | null) => void;
  reset: () => void;
}

function todayIso(): string {
  /*
   * ⛔ persist 기본값 — ★ 「local」이 «아니다». toISOString() 은 UTC 라
   *   KST 00:00~09:00 에는 «어제»가 된다(관리콘솔이 2026-10-01 에 같은 꼴로 운영 결함을 찾았다).
   * ⚠ 지금은 이 값을 «읽는 제품 코드가 0» 이라 드러나지 않는다 —
   *   그 사실과 「읽기 시작하면 고쳐라」를 store/calendar-anchor-dead.guard.test.ts 가 잠근다.
   * ⚠ 그리고 이 화석이 2026-10-01 에 «진단을 한 번 망쳤다» — localStorage 의 그 값을
   *   「사용자가 보던 달」로 읽어 한 바퀴를 돌았다. 그 사정도 그 가드 머리말에 있다.
   */
  return new Date().toISOString().slice(0, 10);
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set, get) => ({
      activeView: 'month',
      anchorDateIso: todayIso(),
      visibleCalendarIds: null,
      onlyMine: false,
      showTeamMembers: false,
      hiddenTypes: [],

      setView: (view) => set({ activeView: view }),
      setAnchorDate: (date) => set({ anchorDateIso: date.toISOString().slice(0, 10) }),
      toggleOnlyMine: () => set((s) => ({ onlyMine: !s.onlyMine })),
      toggleShowTeamMembers: () => set((s) => ({ showTeamMembers: !s.showTeamMembers })),
      toggleType: (type) => set((s) => ({
        hiddenTypes: s.hiddenTypes.includes(type) ? s.hiddenTypes.filter((t) => t !== type) : [...s.hiddenTypes, type],
      })),
      toggleCalendarVisible: (id, allIds) => {
        const cur = get().visibleCalendarIds ?? allIds; // null(전체) → 전체 목록에서 시작
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        // 전체와 동일해지면 null(전체)로 정규화 — 필터 미적용(전체 폴백) 일관.
        set({ visibleCalendarIds: next.length === allIds.length ? null : next });
      },
      setVisibleCalendarIds: (ids) => set({ visibleCalendarIds: ids }),
      reset: () => set({
        activeView: 'month', anchorDateIso: todayIso(), visibleCalendarIds: null,
        onlyMine: false, showTeamMembers: false, hiddenTypes: [],
      }),
    }),
    { name: 'workchat:calendar' },
  ),
);
